// Shared drawing routine for the Votely brand cube — the 3x3 grid of
// colored boxes that anchors the brand identity. Used by:
//
//   - components/AnimatedFavicon.tsx  (animated favicon)
//   - app/dev/cube-opacity/page.tsx   (opacity-tuning mockup)
//   - app/brand/page.tsx              (brand guide + asset downloads)
//
// The geometry mirrors the marketing-nav NavCube (Three.js, 9 elongated
// boxes in a 3x3 grid in the xy plane, each extruded along z) so the
// favicon, mockup, brand-guide assets, and live NavCube all share the same
// visual structure. Color palette below also matches NavCube's
// CELL_COLORS — note `#d4d4d4` for the center cell, NOT the `#e5e5e5` in
// MACRO_CELL_COLORS in lib/constants/colors.ts.

export type V3 = [number, number, number];

/** NavCube/brand cube palette, indexed [row][col] from top-left. */
export const BRAND_PALETTE: string[][] = [
  ['#ff9ea0', '#ff9fff', '#9f9fff'],
  ['#ffcfa1', '#d4d4d4', '#9ffffe'],
  ['#9fff9e', '#d4fe9a', '#ffff9f'],
];

/** Slate-blue brand outline color (matches `--color-secondary` in globals.css). */
export const BRAND_STROKE = '#3b5998';

const CELL = 2 / 3;
const HALF = (CELL / 2) * 0.96; // 4% margin so adjacent boxes don't share edges

type Box = { vertices: V3[]; color: string };

function buildBox(cx: number, cy: number, color: string): Box {
  const v: V3[] = [
    [cx - HALF, cy - HALF, -1], [cx + HALF, cy - HALF, -1],
    [cx + HALF, cy + HALF, -1], [cx - HALF, cy + HALF, -1],
    [cx - HALF, cy - HALF,  1], [cx + HALF, cy - HALF,  1],
    [cx + HALF, cy + HALF,  1], [cx - HALF, cy + HALF,  1],
  ];
  return { vertices: v, color };
}

const BOXES: Box[] = (() => {
  const out: Box[] = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      const cx = (col - 1) * CELL;
      const cy = (1 - row) * CELL;
      out.push(buildBox(cx, cy, BRAND_PALETTE[row][col]));
    }
  }
  return out;
})();

const BOX_FACES: { idx: [number, number, number, number]; normal: V3 }[] = [
  { idx: [1, 0, 3, 2], normal: [0, 0, -1] },
  { idx: [4, 5, 6, 7], normal: [0, 0,  1] },
  { idx: [4, 5, 1, 0], normal: [0, -1, 0] },
  { idx: [3, 2, 6, 7], normal: [0,  1, 0] },
  { idx: [0, 4, 7, 3], normal: [-1, 0, 0] },
  { idx: [5, 1, 2, 6], normal: [ 1, 0, 0] },
];

function rotY(p: V3, t: number): V3 {
  const c = Math.cos(t), s = Math.sin(t);
  return [p[0] * c + p[2] * s, p[1], -p[0] * s + p[2] * c];
}
function rotX(p: V3, t: number): V3 {
  const c = Math.cos(t), s = Math.sin(t);
  return [p[0], p[1] * c - p[2] * s, p[1] * s + p[2] * c];
}

export type CubeOptions = {
  /** Total canvas dimension (square). */
  size: number;
  /** Y-axis rotation in radians. Default 28° (brand resting angle). */
  theta?: number;
  /** X-axis tilt in radians. Default 0.18π (brand resting tilt). Exposed so
   *  the draggable header/footer mark can tilt up/down, not just spin. */
  tilt?: number;
  /**
   * Cell fill opacity 0-1. Default 0.70 — the brand-locked value chosen for
   * the 70% all-faces look. This was picked to read correctly against BOTH
   * the brand light background (#eef1ef) and the brand dark surface
   * (#1c2321), so a single number drives every render without per-context
   * branching.
   */
  cellAlpha?: number;
  /**
   * Render back faces too (NavCube-style volumetric layering). Default true
   * — the brand look is the see-through cube, not the solid one. Pass false
   * only for legacy front-only contexts (none currently in use).
   */
  drawBackFaces?: boolean;
  /** Translate the cube on canvas. Default centered. */
  offset?: { x: number; y: number };
  /** Override the cube's projection scale. Default uses `size`. */
  scaleOverride?: number;
};

/**
 * Renders the brand cube onto the given 2D canvas context. The context is
 * NOT cleared; callers can pre-paint a background or stamp multiple cubes
 * into one canvas (for banner lockups, etc.).
 */
export function drawBrandCube(ctx: CanvasRenderingContext2D, opts: CubeOptions): void {
  const {
    size,
    theta = (Math.PI / 180) * 28,
    tilt = Math.PI * 0.18,
    cellAlpha = 0.70,
    drawBackFaces = true,
    offset = { x: 0, y: 0 },
    scaleOverride,
  } = opts;

  const TILT = tilt;
  const scale = scaleOverride ?? size * 0.30;
  const cx = size / 2 + offset.x;
  const cy = size / 2 + offset.y;

  type Drawable = { cz: number; visible: boolean; pts: [number, number][]; color: string };
  const drawList: Drawable[] = [];

  for (const box of BOXES) {
    const rotated = box.vertices.map((p) => rotX(rotY(p, theta), TILT));
    const screen = rotated.map((p) => [cx + p[0] * scale, cy - p[1] * scale]) as [number, number][];
    for (const f of BOX_FACES) {
      const n = rotX(rotY(f.normal, theta), TILT);
      const verts = f.idx.map((i) => rotated[i]);
      const cz = (verts[0][2] + verts[1][2] + verts[2][2] + verts[3][2]) / 4;
      drawList.push({
        cz,
        visible: n[2] > 0,
        pts: f.idx.map((i) => screen[i]) as [number, number][],
        color: box.color,
      });
    }
  }
  drawList.sort((a, b) => a.cz - b.cz);

  const innerStroke = '#ffffff';
  const innerW = Math.max(0.4, size / 90);

  for (const d of drawList) {
    if (!drawBackFaces && !d.visible) continue;
    ctx.globalAlpha = cellAlpha;
    ctx.fillStyle = d.color;
    ctx.beginPath();
    ctx.moveTo(d.pts[0][0], d.pts[0][1]);
    for (let i = 1; i < 4; i++) ctx.lineTo(d.pts[i][0], d.pts[i][1]);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
    if (cellAlpha > 0 && d.visible) {
      ctx.strokeStyle = innerStroke;
      ctx.lineWidth = innerW;
      ctx.lineJoin = 'miter';
      ctx.stroke();
    }
  }

  // Bounding-cube wireframe in slate-blue. Front edges opaque, back edges at
  // 18% so the cube reads as having interior volume without muddying small
  // sizes.
  const outer: V3[] = [
    [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
    [-1, -1,  1], [1, -1,  1], [1, 1,  1], [-1, 1,  1],
  ];
  const outerRot = outer.map((p) => rotX(rotY(p, theta), TILT));
  const outerScreen = outerRot.map((p) => [cx + p[0] * scale, cy - p[1] * scale]) as [number, number][];
  const EDGES: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [4, 5], [5, 6], [6, 7], [7, 4],
    [0, 4], [1, 5], [2, 6], [3, 7],
  ];
  const EDGE_FACES: [number, number][] = [
    [0, 2], [0, 5], [0, 3], [0, 4],
    [1, 2], [1, 5], [1, 3], [1, 4],
    [2, 4], [2, 5], [3, 5], [3, 4],
  ];
  const faceNz = BOX_FACES.map((f) => rotX(rotY(f.normal, theta), TILT)[2]);
  ctx.strokeStyle = BRAND_STROKE;
  ctx.lineWidth = Math.max(1.2, size / 30);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  EDGES.forEach((edge, i) => {
    const [fa, fb] = EDGE_FACES[i];
    const front = faceNz[fa] > 0 || faceNz[fb] > 0;
    ctx.globalAlpha = front ? 1 : 0.18;
    ctx.beginPath();
    ctx.moveTo(outerScreen[edge[0]][0], outerScreen[edge[0]][1]);
    ctx.lineTo(outerScreen[edge[1]][0], outerScreen[edge[1]][1]);
    ctx.stroke();
  });
  ctx.globalAlpha = 1;
}
